// liveFold.js — folding blocks of the live console's code away.
// -----------------------------------------------
// A line starting with #$ heads a block: the lines below it, up to the next
// #$ line or the end (blank lines at its end stay out). Folded, the block's
// lines leave the editor and are kept with their head line; the head stays,
// with what is written on it. Pure: text in, text and folds out -- the
// console (oscillaLive.js) puts the text in the textarea.
//
// Nested: more $ is a bigger block. A block runs to the next head with as
// many $ or more, so a #$$ block holds the #$ blocks under it -- wrapping
// ten #$ blocks is one line above them:
//
//     #$$ scene 1         folds drums and voices with it
//     #$ drums
//     #$ voices
//     #$$ scene 2
//
// A fold is { line, head, body }: the head's line in the editor's text, what
// the head line said, and the block -- the text that came after the head
// line's end, starting with its "\n", every fold inside it in place. A fold
// made over folds inside it keeps them: `shown` is the block as it was seen
// (theirs folded), `inner` those folds, their lines counted from the head --
// unfolded, it comes back as it was, the inner ones still folded.

export const isFoldHead = line => /^\s*#\$/.test(line);

/** A head's size: the number of $ after its # (0 for a line that is no head). */
export const foldLevel = line => /^\s*#(\$+)/.exec(line)?.[1].length ?? 0;

/** The lines a head at line `head` folds, [first, last], or null when none. */
export function foldSpan(lines, head) {
  const level = foldLevel(lines[head] ?? "");
  if (!level) return null;
  let end = head + 1;
  // to the next head as big or bigger (as many $ or more); smaller ones are inside
  while (end < lines.length && !(isFoldHead(lines[end]) && foldLevel(lines[end]) >= level)) end++;
  let last = end - 1;
  while (last > head && !lines[last].trim()) last--;
  return last > head ? [head + 1, last] : null;
}

/** Character offset of the end of line `n`. */
export function lineEnd(text, n) {
  let at = -1;
  for (let i = 0; i <= n; i++) {
    at = text.indexOf("\n", at + 1);
    if (at === -1) return text.length;
  }
  return at;
}

const lineOf = (text, at) => {
  let n = 0;
  for (let i = 0; i < at; i++) if (text[i] === "\n") n++;
  return n;
};

/**
 * Fold the block under the head at line `head`: { text, fold, from, to } --
 * the text without the block, the fold, and the characters [from, to) that
 * went. null when there is nothing to fold.
 */
export function foldAt(text, head) {
  const lines = text.split("\n");
  const span = foldSpan(lines, head);
  if (!span) return null;
  const from = lineEnd(text, head), to = lineEnd(text, span[1]);
  const body = text.slice(from, to);
  return { text: text.slice(0, from) + text.slice(to), fold: { line: head, head: lines[head], body }, from, to };
}

/**
 * Fold the block under `head`, the folds inside it going in with it:
 * { text, from, to, fold, folds } -- the editor's text without the block,
 * the characters [from, to) that went, the new fold, and all the folds now
 * (those below moved up). null when there is nothing to fold.
 */
export function foldNested(text, folds, head) {
  const lines = text.split("\n");
  const span = foldSpan(lines, head);
  if (!span) return null;
  const from = lineEnd(text, head), to = lineEnd(text, span[1]);
  const shown = text.slice(from, to);
  const inside = folds.filter(f => f.line > head && f.line <= span[1]).sort((a, b) => a.line - b.line);
  const parts = shown.split("\n");                 // [0]: the head line's end; [r]: line head + r
  for (const f of inside) parts[f.line - head] += f.body;
  const fold = { line: head, head: lines[head], body: parts.join("\n") };
  if (inside.length) Object.assign(fold, { shown, inner: inside.map(f => ({ ...f, line: f.line - head })) });
  const gone = span[1] - head;
  const rest = folds.filter(f => !inside.includes(f)).map(f => (f.line > span[1] ? { ...f, line: f.line - gone } : f));
  return { text: text.slice(0, from) + text.slice(to), from, to, fold, folds: [...rest, fold] };
}

/**
 * Bring a fold's block back: { text, at, insert, folds } -- the text with
 * it, where it went and what, and the folds now: the ones inside it still
 * folded, those below moved down.
 */
export function unfoldNested(text, folds, fold) {
  const at = lineEnd(text, fold.line);
  const insert = fold.shown ?? fold.body;
  const added = insert.split("\n").length - 1;
  const rest = folds.filter(f => f !== fold).map(f => (f.line > fold.line ? { ...f, line: f.line + added } : f));
  const inner = (fold.inner || []).map(f => ({ ...f, line: f.line + fold.line }));
  return { text: text.slice(0, at) + insert + text.slice(at), at, insert, folds: [...rest, ...inner] };
}

/**
 * The heads folded, as lines of the full text, the ones inside a fold too
 * (for saving): folded again bottom up (foldNested), they nest as they were.
 */
export function foldedHeadLines(folds) {
  const out = [];
  const walk = (list, base) => {
    let shift = 0;
    for (const f of [...list].sort((a, b) => a.line - b.line)) {
      const at = base + f.line + shift;
      out.push(at);
      if (f.inner) walk(f.inner, at);
      shift += f.body.split("\n").length - 1;          // its whole block, folds inside it too
    }
  };
  walk(folds, 0);
  return out;
}

/** The editor's text with every folded block back in place. */
export function expandText(text, folds) {
  const lines = text.split("\n");
  for (const f of folds) if (f.line < lines.length) lines[f.line] += f.body;
  return lines.join("\n");
}

/** Where a line of the full text is in the editor: its line, or the head of the fold hiding it. */
export function visibleLine(folds, fullLine) {
  let shift = 0;
  for (const f of [...folds].sort((a, b) => a.line - b.line)) {
    const hidden = f.body.split("\n").length - 1;
    const head = f.line + shift;                     // the head's line in the full text
    if (fullLine <= head) break;
    if (fullLine <= head + hidden) return { line: f.line, folded: true };
    shift += hidden;
  }
  return { line: fullLine - shift, folded: false };
}

/** The common start and end of two texts: { p, s, removed, inserted }. */
function textDiff(a, b) {
  let p = 0;
  const max = Math.min(a.length, b.length);
  while (p < max && a[p] === b[p]) p++;
  let s = 0;
  while (s < max - p && a[a.length - 1 - s] === b[b.length - 1 - s]) s++;
  return { p, s, removed: a.slice(p, a.length - s), inserted: b.slice(p, b.length - s) };
}

/**
 * The folds after the editor's text changed from `oldText` to `newText`:
 *   { folds, unfolded, restore }
 * Lines moved by the edit carry their folds with them. A head line that was
 * typed on keeps its fold while it still starts with #$. `unfolded`: folds
 * whose block came back right after their head (an undo of the fold) --
 * they are folds no longer. `restore`: blocks whose head line stays but is a
 * head no longer (its #$ typed away), with where to put them back
 * ({ at, text }). `deleted`: folds whose head line was deleted -- the block
 * goes with it, as in any editor; reattach() brings it back on an undo.
 */
export function moveFolds(folds, oldText, newText) {
  const { p, s } = textDiff(oldText, newText);
  const startLine = lineOf(oldText, p);
  const oldEnd = lineOf(oldText, oldText.length - s);
  const newEnd = lineOf(newText, newText.length - s);
  const delta = newEnd - oldEnd;
  const newLines = newText.split("\n");
  const kept = [], unfolded = [], restore = [], deleted = [];
  const taken = new Set();
  for (const f of [...folds].sort((a, b) => a.line - b.line)) {
    // the block back after its head: the fold was undone
    const e = lineEnd(oldText, f.line);
    const shown = f.shown ?? f.body;
    if (newText.length === oldText.length + shown.length
        && newText === oldText.slice(0, e) + shown + oldText.slice(e)) {
      unfolded.push(f);
      // the folds inside it: folds again, where the block put them
      for (const i of f.inner || []) kept.push({ ...i, line: f.line + i.line });
      continue;
    }
    if (f.line < startLine) { kept.push(f); continue; }
    if (f.line > oldEnd) { kept.push({ ...f, line: f.line + delta }); continue; }
    // the edit touched the head's line: find the head in what replaced it --
    // the same words; or, when the edit stayed on that one line (typing on
    // the head), the line itself while it is still a head
    let k = -1;
    for (let i = startLine; i <= newEnd && k < 0; i++) if (!taken.has(i) && newLines[i] === f.head) k = i;
    const oneLine = startLine === oldEnd && oldEnd === newEnd;
    if (k < 0 && oneLine && !taken.has(f.line) && isFoldHead(newLines[f.line] ?? "")) k = f.line;
    if (k >= 0) { taken.add(k); kept.push({ ...f, line: k, head: newLines[k] }); continue; }
    // the head line was deleted (with others, or the lines around it joined):
    // the block goes with it
    if (!oneLine) { deleted.push(f); continue; }
    // typed on and no longer a head: the block comes back below its line
    // (inside the stretch that changed: at the start of the line the change begins on)
    let at = e <= p ? e : e >= oldText.length - s ? e + newText.length - oldText.length
      : p === 0 ? 0 : newText.lastIndexOf("\n", p - 1) + 1;   // (lastIndexOf at -1 still looks at 0)
    const atLineStart = at === 0 || newText[at - 1] === "\n";
    if (!atLineStart) { const nl = newText.indexOf("\n", at); at = nl === -1 ? newText.length : nl; }
    restore.push({ at, text: atLineStart ? f.body.slice(1) + "\n" : f.body, fold: f });
  }
  return { folds: kept, unfolded, restore, deleted };
}

/**
 * Marks on lines (line -> value) after the text changed from `oldText` to
 * `newText`: a line moved by the edit takes its mark along; a line typed on
 * (the edit on that one line) keeps it; in a change of several lines, a line
 * still there word for word keeps it, and the rest are dropped.
 */
export function moveLineMarks(marks, oldText, newText) {
  const out = new Map();
  if (!marks.size || oldText === newText) return new Map(marks);
  const { p, s } = textDiff(oldText, newText);
  const startLine = lineOf(oldText, p);
  const oldEnd = lineOf(oldText, oldText.length - s);
  const newEnd = lineOf(newText, newText.length - s);
  const oneLine = startLine === oldEnd && oldEnd === newEnd;
  const oldLines = oldText.split("\n"), newLines = newText.split("\n");
  const taken = new Set();
  for (const [line, v] of [...marks].sort((a, b) => a[0] - b[0])) {
    if (line < startLine) out.set(line, v);
    else if (line > oldEnd) out.set(line + newEnd - oldEnd, v);
    else if (oneLine) out.set(line, v);
    else {
      for (let i = startLine; i <= newEnd; i++) {
        if (!taken.has(i) && newLines[i] === oldLines[line]) { taken.add(i); out.set(i, v); break; }
      }
    }
  }
  return out;
}

/**
 * An undo that brings back the head of a deleted fold: the fold again, on
 * that line -- [fold], one for each head of `deleted` now back in the lines
 * the edit wrote.
 */
export function reattach(deleted, folds, oldText, newText) {
  const { p, s } = textDiff(oldText, newText);
  const startLine = lineOf(oldText, p);
  const newEnd = lineOf(newText, newText.length - s);
  const lines = newText.split("\n");
  const out = [];
  const used = new Set(folds.map(f => f.line));
  for (const d of deleted) {
    for (let i = startLine; i <= newEnd; i++) {
      if (lines[i] === d.head && !used.has(i)) { out.push({ ...d, line: i }); used.add(i); break; }
    }
  }
  return out;
}

/**
 * A redo of a fold: a block that an undo brought back (one of `undone`)
 * taken out again right after its head. The fold, back on its head's
 * line, or null.
 */
export function redoneFold(undone, oldText, newText) {
  const lines = oldText.split("\n");
  for (const d of undone) {
    const shown = d.shown ?? d.body;
    if (oldText.length - newText.length !== shown.length) continue;
    for (let k = 0; k < lines.length; k++) {
      if (lines[k] !== d.head) continue;
      const e = lineEnd(oldText, k);
      if (oldText.slice(e, e + shown.length) === shown
          && newText === oldText.slice(0, e) + oldText.slice(e + shown.length)) return { ...d, line: k };
    }
  }
  return null;
}

/**
 * The block the caret's line is in, [first, last] -- what Ctrl+Shift+B
 * shows alone: from the nearest #$ head above it to the end of that head's
 * block (the innermost: a #$ inside a #$$), else the lines round it up to a
 * blank line either side. A blank line outside any block: itself.
 */
export function focusSpan(lines, line) {
  for (let h = Math.min(line, lines.length - 1); h >= 0; h--) {
    if (!isFoldHead(lines[h])) continue;
    // this head's block runs to the next head as big or bigger (blank lines
    // at its end its own too); past it, a bigger one above may hold it still
    let end = h + 1;
    while (end < lines.length && !(isFoldHead(lines[end]) && foldLevel(lines[end]) >= foldLevel(lines[h]))) end++;
    if (line < end) return [h, foldSpan(lines, h)?.[1] ?? h];
  }
  if (!lines[line]?.trim()) return [line, line];
  let a = line, b = line;
  while (a > 0 && lines[a - 1].trim() && !isFoldHead(lines[a - 1])) a--;
  while (b < lines.length - 1 && lines[b + 1].trim() && !isFoldHead(lines[b + 1])) b++;
  return [a, b];
}
